import './external/focus-visible.min.js';

function isInDisabledContext(element) {
    return !!element.closest('.disabled-element, .disabled-link');
}

function restoreDisabledState(target) {
    const formControl = target;
    if (formControl.dataset.disabledReadonly === 'true') {
        formControl.readOnly = false;
        delete formControl.dataset.disabledReadonly;
    }
    if (formControl.dataset.disabledDisabled === 'true') {
        formControl.disabled = false;
        delete formControl.dataset.disabledDisabled;
    }
    if (formControl.dataset.disabledSlider === 'true') {
        const { prevTabindex } = formControl.dataset;
        if (prevTabindex) {
            formControl.setAttribute('tabindex', prevTabindex);
        } else {
            formControl.removeAttribute('tabindex');
        }
        formControl.removeAttribute('aria-disabled');
        delete formControl.dataset.disabledSlider;
        delete formControl.dataset.prevTabindex;
    }
}

export const accessibility = {
    init() {
        let isKeyboardUser = false; // Track if the user is using keyboard navigation
        let modalStack = []; // Handle click events- moves focus to previsouly clicked element
        accessibility.setupColorboxAria(); // Setup ARIA attributes for Colorbox modals

        // When Colorbox modal opens
        $(document).on('cbox_complete', function () {
            let triggerElement = $.colorbox.element ? $($.colorbox.element) : null;
            if (triggerElement) {
                accessibility.trapModalFocusByClass('cboxContent');
            }
        });

        $(document).on('click keydown', '[data-open-colorbox]', function () {
            modalStack.push(this); // push current trigger to stack
        });

        // When Colorbox modal closes
        $(document).on('cbox_closed', function () {
            if (modalStack.length > 0) {
                const lastTriggerEl = modalStack.pop(); // get the most recent opener
                if (lastTriggerEl && typeof lastTriggerEl.focus === 'function') {
                    setTimeout(() => lastTriggerEl.focus(), 0); // restore focus
                }
            }

            accessibility.releaseModalFocus();
        });

        // Detect keyboard navigation (Tab key)
        document.addEventListener('keydown', function (event) {
            if (event.key === 'Tab') {
                isKeyboardUser = true;
            }
        });

        // Detect mouse and touch interactions
        document.addEventListener('mousedown', function () {
            isKeyboardUser = false;
        });

        document.addEventListener('touchstart', function () {
            isKeyboardUser = false;
        });

        // Handle focus events
        document.addEventListener('focusin', function (event) {
            let { target } = event;

            // Prevent form elements from being editable inside disabled containers
            let isDisabled = isInDisabledContext(target);

            if (isDisabled && target.classList.contains('ui-slider-handle')) {
                // Disable slider handle keyboard interaction
                if (target.dataset.disabledSlider !== 'true') {
                    target.dataset.prevTabindex = target.getAttribute('tabindex') || '';
                    target.setAttribute('tabindex', '-1');
                    target.setAttribute('aria-disabled', 'true');
                    target.dataset.disabledSlider = 'true';
                }
                target.blur();
            } else if (!isDisabled) {
                // Restore form elements that were disabled by us when container was disabled
                restoreDisabledState(target);
            }

            // Check if the focused element or any of its ancestors has opacity: 0
            let isInvisible = false;
            let current = target;
            while (current) {
                if (window.getComputedStyle(current).opacity === '0') {
                    isInvisible = true;
                    break;
                }
                current = current.parentElement;
            }

            if (isKeyboardUser && isInvisible) {
                let parentWithClass = accessibility.findNearestOpacityChildParent(target);
                if (parentWithClass) {
                    parentWithClass.classList.add('focus-parent-visible');
                }

                // Apply the same class to custom radio/checkbox parent
                let customControlParent = accessibility.findCustomControlParent(target);
                if (customControlParent) {
                    customControlParent.classList.add('focus-parent-visible');
                }
            }

            // Remove .focus-visible class added by focus-visible.js if not using Tab
            if (!isKeyboardUser) {
                target.classList.remove('focus-visible');
            }
        });

        // Handle focus-out events
        document.addEventListener('focusout', function (event) {
            let { target } = event;

            // Restore disabled state when element leaves a disabled container
            if (!isInDisabledContext(target)) {
                restoreDisabledState(target);
            }

            let parentWithClass = accessibility.findNearestOpacityChildParent(target);
            if (parentWithClass) {
                parentWithClass.classList.remove('focus-parent-visible');
            }

            // Remove focus class from custom radio/checkbox parent
            let customControlParent = accessibility.findCustomControlParent(target);
            if (customControlParent) {
                customControlParent.classList.remove('focus-parent-visible');
            }
        });

        // Observe class changes on the document body so disabled-container
        // state is kept in sync even when Tab cannot reach disabled=true elements.
        const disabledObserver = new MutationObserver(function () {
            accessibility.updateDisabledContainerElements();
        });
        disabledObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
            subtree: true,
        });
        // Apply immediately for elements already in the DOM on page load.
        accessibility.updateDisabledContainerElements();
    },
    findNearestOpacityChildParent(element) {
        // Function to find the nearest ancestor with the class "opacity-child-element"
        let current = element;
        while (current) {
            if (current.classList.contains('opacity-child-element')) {
                return current; // Return the first matching ancestor
            }
            current = current.parentElement; // Move up the tree
        }
        return current; // If no matching ancestor is found
    },
    findCustomControlParent(element) {
        // Function to find the nearest parent with class "form-row__form-control" or "form-row__toggle-button"
        let current = element;
        while (current) {
            if (
                current.classList.contains('form-row__form-control') ||
                current.classList.contains('form-row__toggle-button')
            ) {
                return current; // Return the first matching parent
            }
            current = current.parentElement; // Move up the tree
        }
        return current; // If no matching ancestor is found
    },
    disableFocusOutsideModal(modalWrapper) {
        // Function to disable focus on elements outside the modal - focus trap
        const allElements = document.querySelectorAll('body *');
        allElements.forEach((el) => {
            if (el === modalWrapper || modalWrapper.contains(el)) {
                if (el.hasAttribute('tabindex') && el.getAttribute('data-disabled-tab') === 'true') {
                    el.removeAttribute('tabindex');
                    el.removeAttribute('data-disabled-tab');
                }
            } else if (!el.hasAttribute('data-disabled-tab')) {
                if (el.hasAttribute('tabindex')) {
                    el.setAttribute('data-original-tabindex', el.getAttribute('tabindex'));
                }
                el.setAttribute('tabindex', '-1');
                el.setAttribute('data-disabled-tab', 'true');
            }
        });
    },
    trapFocusInsideModal(modalWrapper) {
        const focusableSelectors = `
                a[href],
                area[href],
                input:not([disabled]):not([type="hidden"]),
                select:not([disabled]),
                textarea:not([disabled]),
                button:not([disabled]),
                iframe,
                object,
                embed,
                [tabindex]:not([tabindex="-1"]),
                [contenteditable]
            `;

        let focusableElements = Array.from(modalWrapper.querySelectorAll(focusableSelectors)).filter(
            (el) => el.offsetParent !== null
        );

        if (focusableElements.length === 0) {
            modalWrapper.setAttribute('tabindex', '0');
            focusableElements = [modalWrapper];
        }

        const firstEl = focusableElements[0];
        const lastEl = focusableElements[focusableElements.length - 1];

        // Automatically focus first element
        if (firstEl) {
            firstEl.focus();
        }

        modalWrapper.addEventListener('keydown', function (e) {
            if (e.key === 'Tab') {
                const activeEl = document.activeElement;

                // 🛑 Special condition For nettiauto Advance Search when focus is on "statusType_0"
                if (activeEl.id === 'statusType_0' && modalWrapper.classList.contains('advance-search-modal')) {
                    e.preventDefault();
                    firstEl.focus();
                    return; // Exit early
                }

                if (e.shiftKey) {
                    // SHIFT + TAB
                    if (activeEl === firstEl) {
                        e.preventDefault();
                        lastEl.focus();
                    }
                } else if (activeEl === lastEl) {
                    // TAB only
                    e.preventDefault();
                    firstEl.focus();
                }
            }
        });
    },
    restoreFocusOutsideModal() {
        const disabledElements = document.querySelectorAll('[data-disabled-tab]');
        disabledElements.forEach((el) => {
            if (el.hasAttribute('data-original-tabindex')) {
                el.setAttribute('tabindex', el.getAttribute('data-original-tabindex'));
                el.removeAttribute('data-original-tabindex');
            } else {
                el.removeAttribute('tabindex');
            }
            el.removeAttribute('data-disabled-tab');
        });
    },
    trapModalFocusByClass(modalSelector) {
        setTimeout(() => {
            let modalWrapper = document.querySelector(`.${modalSelector}`) || document.getElementById(modalSelector);
            if (modalWrapper) {
                accessibility.disableFocusOutsideModal(modalWrapper);
                accessibility.trapFocusInsideModal(modalWrapper);
            }
        }, 100);
    },
    releaseModalFocus() {
        accessibility.restoreFocusOutsideModal();
    },
    updateDisabledContainerElements() {
        const nonReadonlyTypes = new Set(['checkbox', 'radio', 'range', 'file']);
        const formSelector = 'input, textarea, select';

        // Disable form elements inside disabled containers
        document.querySelectorAll('.disabled-element, .disabled-link').forEach((container) => {
            container.querySelectorAll(formSelector).forEach((el) => {
                const formControl = el;
                if (
                    formControl.tagName === 'SELECT' ||
                    (formControl.tagName === 'INPUT' && nonReadonlyTypes.has(formControl.type))
                ) {
                    if (!formControl.disabled) {
                        formControl.disabled = true;
                        formControl.dataset.disabledDisabled = 'true';
                    }
                } else if (!formControl.readOnly) {
                    formControl.readOnly = true;
                    formControl.dataset.disabledReadonly = 'true';
                }
            });
        });

        // Restore form elements no longer inside disabled containers
        document.querySelectorAll('[data-disabled-readonly]').forEach((el) => {
            const formControl = el;
            if (!isInDisabledContext(formControl)) {
                formControl.readOnly = false;
                delete formControl.dataset.disabledReadonly;
            }
        });
        document.querySelectorAll('[data-disabled-disabled]').forEach((el) => {
            const formControl = el;
            if (!isInDisabledContext(formControl)) {
                formControl.disabled = false;
                delete formControl.dataset.disabledDisabled;
            }
        });
    },
    updateDisabledLinkAttributes() {
        document.querySelectorAll('.disabled-link').forEach((el) => {
            el.setAttribute('aria-disabled', 'true');
            el.setAttribute('aria-hidden', 'true');
            el.setAttribute('tabindex', '-1');

            // If it's a form element, also set real 'disabled'
            if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'OPTGROUP', 'OPTION', 'FIELDSET'].includes(el.tagName)) {
                el.setAttribute('disabled', 'true');
            }
        });

        // Clean up elements that had the class removed
        document.querySelectorAll('[aria-disabled="true"]:not(.disabled-link)').forEach((el) => {
            el.removeAttribute('aria-disabled');
            el.removeAttribute('aria-hidden');
            el.removeAttribute('tabindex');

            if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'OPTGROUP', 'OPTION', 'FIELDSET'].includes(el.tagName)) {
                el.removeAttribute('disabled');
            }
        });
    },
    setupColorboxAria() {
        // Ensure Colorbox modals have proper ARIA attributes
        $.colorbox.settings.onComplete = function () {
            const modal = document.getElementById('colorbox');
            if (!modal) return;

            // Show modal to screen reader
            modal.setAttribute('role', 'dialog');
            modal.setAttribute('aria-modal', 'true');
            modal.setAttribute('aria-hidden', 'false'); // Explicitly expose modal

            // Hide rest of the page from screen readers
            const pageContent = document.querySelectorAll('main, header, footer, [aria-hidden="false"]:not(#colorbox)');
            pageContent.forEach((el) => el.setAttribute('aria-hidden', 'true'));

            // Generate a unique ID
            const uniqueId = `modal-${Date.now()}`;

            // Title handling (optional)
            const titleEl = modal.querySelector('.modal-title');
            if (titleEl) {
                const titleId = `${uniqueId}-title`;
                titleEl.setAttribute('id', titleId);
                modal.setAttribute('aria-labelledby', titleId);
            } else {
                modal.removeAttribute('aria-labelledby');
            }

            // Description handling (optional)
            const descEl = modal.querySelector('.modal-desc');
            if (descEl) {
                const descId = `${uniqueId}-desc`;
                descEl.setAttribute('id', descId);
                modal.setAttribute('aria-describedby', descId);
            } else {
                modal.removeAttribute('aria-describedby');
            }
        };

        $.colorbox.settings.onClosed = function () {
            const pageContent = document.querySelectorAll('main, header, footer');
            pageContent.forEach((el) => el.setAttribute('aria-hidden', 'false'));
        };
    },
};

$(function () {
    const skipButton = document.getElementById('skip-button');
    const mainContent = document.getElementById('main-content');
    if (skipButton && mainContent) {
        skipButton.addEventListener('click', function () {
            mainContent.focus({ preventScroll: false }); // Ensure scrolls into view
            mainContent.scrollIntoView({ behavior: 'smooth' }); // Smooth scroll
        });
    }
});
